import { defineNuxtPlugin, useRequestFetch, useRuntimeConfig } from 'nuxt/app';
import type { FetchOptions } from 'ofetch';
import { createOpenFetch, useOpenapiBaseURL } from '#imports';
import { setupPortalSession } from '@modules/portal/utils/setupUserSession';

export default defineNuxtPlugin({
  enforce: 'pre', // clients will be ready to use by other plugins, Pinia stores etc.
  setup() {
    const { public: { openFetch: clients }, apiClientUserAgent } = useRuntimeConfig();
    const localFetch = useRequestFetch();
    const baseURL = useOpenapiBaseURL();
    const retryStatusCodes = import.meta.server ? undefined : [401, 403, 500];
    const defaultOptions: FetchOptions = {
      baseURL,
      retry: import.meta.server ? false : 1,
      retryStatusCodes,
      retryDelay: 500,
      timeout: import.meta.server ? 1000 : 60000,
      // Temporary fix for json output from BE
      headers: {
        Accept: 'application/json',
      },
      onRequest({ options }) {
        if (import.meta.server && apiClientUserAgent) {
          options.headers.set('User-Agent', apiClientUserAgent);
        }
      },
      onResponse({ response }) {
        if (import.meta.client && response.status === 202 && response.headers.get('x-queue-status') === 'queued') {
          throw {
            statusCode: 202,
            statusMessage: 'queued',
          };
        }
      },
      async onResponseError({ response, options }) {
        // Handle specific error codes
        const sessionExpired = response.status === 400 && response._data[0]?.errors[0].code === '001'; // Assuming 001 is the error code for session expiration
        if ((retryStatusCodes?.includes(response.status) || sessionExpired) && !import.meta.server && options.retry !== false) {
          await setupPortalSession(true);
        }

        if (response.status >= 500) {
          response._data = [{ errors: [{ message: 'Internal Server Error', code: 'web_error_technical' }] }];
        }
      },
    };

    return {
      provide: Object.entries(clients).reduce((acc, [name, options]) => ({
        ...acc,
        [name]: createOpenFetch((localOptions) => ({
          ...options,
          ...defaultOptions,
          ...localOptions,
        }), localFetch),
      }), {}),
    };
  },
});
